/**
 * Somebody else's machine guns (G36).
 *
 * `SIMSNDS-04` is VERIFIED as the guns of an aeroplane that is not the player's:
 * bank program 7, note 12 → fnum 343 / block 0, keyed on the **same 118 ms /
 * 101 ms cadence as the player's own cue 1**, on its own voice, with the carrier
 * total level stepping `9 → 18` as the Fokker that was shooting fell back
 * (`docs/formats/sound-music.md` § "Gunfire, jams and hits", captured while von
 * Richthofen shot the player down). It has sat reserved and unwired since that
 * capture because the port had no "another aeroplane is firing" signal; it has
 * one now (`encounter-views.ts`'s `AircraftView.firingNow`).
 *
 * **Three decisions, each following an existing precedent rather than inventing:**
 *
 * 1. **A baked one-shot per crack, not a live OPL voice.** The capture measures a
 *    *total-level ladder*, which is a register write; but `sfx-map.ts` reserves a
 *    baked cue id, and `flak-audio.ts`'s header already settled the reconciliation
 *    for exactly this case — *"the same **shape** of distance model … comes out as
 *    a continuous 0..1 gain multiplier on a per-shot `GainNode` instead of a
 *    ladder-index register write."* This module is that sentence applied a second
 *    time, so the two distance-attenuated one-shot layers in the mix work alike.
 *
 * 2. **The cadence is the original's, not the sim's.** The port's guns run at
 *    `gunRoundsPerSecond: 55` where the capture puts the original at **8.47
 *    rounds/s** — a 6.5× gap that is a gunnery-lane question and not this lane's
 *    to close (`audio-plan.md` §6b). So, exactly as the player's own layer does
 *    in `engine.ts`'s `updateGunfire`, this ignores the sim's round count
 *    entirely and retriggers off a *boolean* at `GUN_SHOT_INTERVAL_S`. Playing one
 *    shot per `RoundRecord` would put six and a half times too many cracks in the
 *    air.
 *
 * 3. **No toggle.** Unlike `dive-whine.ts` this is not an invention — it is a cue
 *    the original plays, in a situation the original plays it in — so it rides no
 *    DISPLAY row and is on whenever the mix is.
 *
 * ## The second tenant: guns that are not on an aeroplane (2026-09-01)
 *
 * A Zeppelin's four stations and an escort sloop's two Maxims shoot at the
 * player and had been **silent since the day each shipped** — not attenuated to
 * nothing, not mixed low: nothing was ever played, because the only signal this
 * pool had was `AircraftView.firingNow` and neither of those is an aeroplane.
 * `frame-signals.ts`'s `firingBatteryRangesM` is now the other half of that
 * signal, and the two arrive here as one list (`firingGunRangesM`).
 *
 * They get **this** cue at **this** level rather than one of their own, and that
 * is the recovered reading rather than a convenience: the original's bank holds
 * exactly two gun cues, the player's own and *somebody else's*, and the capture
 * that identified cue 4 already puts it 6.7 dB under cue 1 in the bank's own
 * carrier levels (`audio-plan.md` §571). A Maxim in a splinter tub is somebody
 * else's gun. Nothing in this module adds gain to make a ship louder, and the
 * only thing that decides how loud she is is `enemyGunGain` on her slant range —
 * so the same curve that keeps a distant friendly quiet keeps a distant ship
 * quiet, and a sloop 300 m below the fight is as loud as a Fokker 300 m astern.
 * Ranking is unchanged and nearest-first, so a close battery can take a voice
 * off a far aeroplane; that is the rule this pool already had.
 *
 * The gain curve lives here rather than beside `flakBurstGain` in
 * `continuous-params.ts` because that file is already at the project's line-count
 * ceiling, which is the same reason `flak-audio.ts` exists as its own module.
 */

import { ENEMY_AUDIBLE_RANGE_M, ENEMY_CLOSEST_RANGE_M } from "./engine-voice.ts";
import { advanceRetrigger, GUN_SHOT_INTERVAL_S, type RetriggerState } from "./continuous-params.ts";
import { playOneShot } from "./playback.ts";
import { ENEMY_GUN_CUE } from "./sfx-map.ts";
import type { BufferCache } from "./buffer-cache.ts";

/**
 * How many other aeroplanes' guns are worth spending voices on at once. Three,
 * matching `FLAK_BURST_MAX_CONCURRENT`, and for the same reason: a turning fight
 * can have half a dozen aeroplanes shooting in the same frame, and past the third
 * the mix is a texture rather than three legible sources.
 */
export const ENEMY_GUN_MAX_CONCURRENT = 3;

/**
 * INFERRED range → gain, the same normalised log-ratio shape as
 * `continuous-params.ts`'s `flakBurstGain` and `engine-voice.ts`'s
 * `enemyLevelIndexForRange`. `1` at or inside the floor, `0` at or beyond the
 * ceiling (silent, and dropped before it can spend a concurrency slot).
 *
 * The two bounds are **not** picked freehand and are not flak's: they are the
 * enemy *engine* voice's own `ENEMY_AUDIBLE_RANGE_M` / `ENEMY_CLOSEST_RANGE_M`,
 * imported rather than copied. That is the whole justification — the capture says
 * cue 4 is attenuated by "the same distance attenuation the enemy-engine voices
 * use", so it gets the same two numbers, and a lane that re-measures the engine
 * ladder's range mapping moves both layers together instead of leaving them
 * disagreeing about how far away 1,500 m sounds.
 */
export function enemyGunGain(distanceM: number): number {
  if (!Number.isFinite(distanceM) || distanceM > ENEMY_AUDIBLE_RANGE_M) return 0;
  const clamped = Math.max(ENEMY_CLOSEST_RANGE_M, distanceM);
  return Math.log(ENEMY_AUDIBLE_RANGE_M / clamped) / Math.log(ENEMY_AUDIBLE_RANGE_M / ENEMY_CLOSEST_RANGE_M);
}

/**
 * What each concurrency slot's retrigger accumulator is set to when it restarts,
 * as a fraction of one shot interval — so slot 0 (`1`) is already due and cracks
 * on the very frame its shooter opens up, the way `engine.ts`'s `updateGunfire`
 * puts the player's first round on the rising edge rather than an interval later.
 *
 * The port does not know *which* aeroplane a crack belongs to — it knows how many
 * are firing and how far away each is — so the slots are ranked by range rather
 * than identified. Restarting them all together would make two shooters key on
 * the same sample and sound like one gun firing louder; a third of an interval
 * apart they interleave. That stagger is a mixing choice and nothing in the
 * capture speaks to it: the capture has one enemy shooting.
 */
const SLOT_RESTART_FRACTION: readonly number[] = [1, 2 / 3, 1 / 3];

export interface EnemyGunVoicePool {
  /**
   * Slant ranges in metres to every gun that is not the player's and fired this
   * frame (`frame-signals.ts`'s `firingGunRangesM` — the other aeroplanes and
   * the ships' and Zeppelins' batteries), and the frame's own `dt`.
   */
  update(dtS: number, rangesM: readonly number[] | undefined): void;
}

/** Nothing is firing — a module constant rather than an inline `[]`, which in a 60 Hz loop is an allocation a second (`lessons.md`, crash-sites lane). */
const NO_RANGES: readonly number[] = [];

export function createEnemyGunVoicePool(
  context: AudioContext,
  cache: BufferCache<AudioBuffer>,
  bus: GainNode,
  isDisposed: () => boolean,
): EnemyGunVoicePool {
  const slots: RetriggerState[] = SLOT_RESTART_FRACTION.map(() => ({ accumulatorS: 0 }));
  const wasActive: boolean[] = SLOT_RESTART_FRACTION.map(() => false);
  let active = 0;
  return {
    update(dtS, rangesM) {
      const ranges = rangesM ?? NO_RANGES;
      // Nearest first, so a crowded fight spends its budget on the shooters most
      // worth hearing — `flak-audio.ts`'s rule, and the same silent drop for
      // anything already out of earshot. Sorting is skipped for the common
      // nought-or-one-shooter frame rather than copying the array every tick.
      const sorted = ranges.length > 1 ? [...ranges].sort((a, b) => a - b) : ranges;
      for (let slot = 0; slot < SLOT_RESTART_FRACTION.length; slot++) {
        const rangeM = sorted[slot];
        const firing = rangeM !== undefined;
        // Re-seed the stagger on every restart: `advanceRetrigger` parks an
        // inactive slot at zero, so without this a lull would phase-lock every
        // slot together for the next burst.
        const restarting = firing && !wasActive[slot];
        const state = restarting
          ? { accumulatorS: (SLOT_RESTART_FRACTION[slot] ?? 0) * GUN_SHOT_INTERVAL_S }
          : (slots[slot] as RetriggerState);
        wasActive[slot] = firing;
        const shot = advanceRetrigger(state, dtS, firing, GUN_SHOT_INTERVAL_S);
        slots[slot] = shot.next;
        if (!shot.fire || rangeM === undefined) continue;
        const gain = enemyGunGain(rangeM);
        if (gain <= 0 || active >= ENEMY_GUN_MAX_CONCURRENT) continue;
        active++;
        playOneShot(context, cache, ENEMY_GUN_CUE, bus, 1, isDisposed, gain, () => {
          active = Math.max(0, active - 1);
        });
      }
    },
  };
}
